// The Assistant's review card for a record whose CONTENT it wrote — a blog
// post, a product email, a job description, a product page.
//
// ONE component for both Assistant surfaces (the builder's Run view and the
// downloaded runtime): the hosts differ only in how they reach the server, so
// the two calls are injected (`onRun`, `onRefine`) and everything else lives
// here once.
//
// Two shapes, chosen by the server's `publish_mode`:
//   - 'draft'  (the default) — compact: what was written, an expandable
//     preview, and one button that saves it as a draft to be reviewed later
//     on the object's own screen.
//   - 'review' — the person asked to see it before it goes out: the full
//     content, editable in place (rich text in the same editor forms use),
//     an "Ask for changes" box that revises it without a chat turn, and
//     Publish / Save as draft.
//
// The server re-applies the draft rule on every run, so `publish` here is a
// REQUEST, not a decision: a card cannot save a live record the server would
// not.

import { useMemo, useState } from 'react'
import type { CSSProperties } from 'react'
import RichTextField from '../txn/widgets/RichTextField'
import { richPlainText, sanitizeRichHtml } from '../richContent'

export interface AuthoringField {
  key: string
  label: string
  required?: boolean
  rich?: boolean
  long?: boolean
  maxlen?: number
}

export interface AuthoringInfo {
  status_column: string
  draft_value: string
  live_value: string
  source?: string
  /** Column holding the cover image URL (and its alt text), when there is one. */
  image_field?: string
  image_alt_field?: string
  publish_mode: 'draft' | 'review'
  /** Fields the Assistant's writer filled. */
  composed: string[]
  /** Every content field of the object, in form order. */
  fields: AuthoringField[]
  /** What the server adjusted while staging (e.g. a field trimmed to its limit). */
  notes?: string[]
}

export interface AuthoringColumn {
  key: string
  label: string
  required?: boolean
  auto?: boolean
  rich?: boolean
  long?: boolean
  maxlen?: number
}

export interface AuthoringRunOutcome {
  ok: boolean
  error?: string
  result?: unknown
}

export interface AuthoringRefineOutcome {
  ok: boolean
  values?: Record<string, unknown>
  notes?: string[]
  error?: string
}

export interface ComposedContentCardProps {
  objLabel: string
  action: string
  summary?: string
  columns: AuthoringColumn[]
  values: Record<string, unknown>
  authoring: AuthoringInfo
  /** Set when the card was already run (restored from the transcript). */
  ran?: 'ok' | 'error'
  ranPublished?: boolean
  runError?: string
  onRun: (values: Record<string, unknown>, publish: boolean) => Promise<AuthoringRunOutcome>
  /** Revise the content with the person's feedback; omitted = not offered. */
  onRefine?: (values: Record<string, unknown>, feedback: string) => Promise<AuthoringRefineOutcome>
}

const BORDER = '#e5e7eb'
const MUTED = '#6b7280'
const GREEN = '#16a34a'

const btn = (bg: string, fg: string, busy: boolean, outline = false, disabled = false): CSSProperties => ({
  padding: '8px 14px', fontSize: 14, fontWeight: 600, borderRadius: 8,
  border: outline ? `1px solid ${BORDER}` : 'none', background: bg, color: fg,
  cursor: busy ? 'wait' : disabled ? 'not-allowed' : 'pointer', opacity: busy || disabled ? 0.6 : 1,
})

const linkBtn: CSSProperties = {
  background: 'none', border: 'none', padding: 0, fontSize: 13, color: '#2563eb',
  cursor: 'pointer', fontWeight: 600,
}

function str(v: unknown): string {
  return v == null ? '' : String(v)
}

/** A failed request as a sentence a person can act on — the server's own
 *  reason when it gave one (`detail`, FastAPI's 422 list, `error`), never the
 *  transport's "Request failed with status code 500". Exported for the hosts. */
export function describeRequestError(e: unknown, fallback = 'Something went wrong.'): string {
  const err = e as {
    code?: string
    message?: string
    response?: { status?: number; data?: { detail?: unknown; error?: unknown; message?: unknown } }
  }
  const data = err?.response?.data
  const detail = data?.detail ?? data?.error ?? data?.message
  if (typeof detail === 'string' && detail.trim()) return detail.trim()
  if (Array.isArray(detail) && detail.length) {
    return detail.map((d) => {
      const item = d as { msg?: string; loc?: unknown[] }
      const where = Array.isArray(item.loc) ? item.loc.filter((x) => x !== 'body').join('.') : ''
      return `${where ? `${where}: ` : ''}${item.msg || JSON.stringify(d)}`
    }).join('; ')
  }
  if (err?.code === 'ECONNABORTED') return 'The server took too long to answer. Try again.'
  if (err && !err.response && err.message === 'Network Error') return 'Could not reach the server. Check your connection and try again.'
  const status = err?.response?.status
  if (status === 401) return 'Your session has expired. Sign in again and retry.'
  if (status === 403) return 'You are not allowed to do this.'
  if (status) return `The server refused the request (HTTP ${status}).`
  return e instanceof Error && e.message ? e.message : fallback
}

/** Whether a save came back PARKED for approval rather than written — an
 *  object bound to an approval workflow stages the record until approved. */
function pendingApproval(result: unknown): string {
  if (!result || typeof result !== 'object') return ''
  const r = result as Record<string, unknown>
  if (r.approval_pending || r.status === 'pending') {
    return str(r.message) || 'Sent for approval — it is saved once approved.'
  }
  return ''
}

// Pictures the Assistant placed (infographics, generated images) are full
// width in the article; in the card they must fit it.
const PREVIEW_CSS = `
.twasta-composed-rich img { max-width: 100%; height: auto; border-radius: 6px; }
.twasta-composed-rich figure { margin: 10px 0; }
.twasta-composed-rich figcaption { font-size: 13px; color: ${MUTED}; margin-top: 4px; }
`

/** The content, read-only: headings, paragraphs and lists as they will look. */
function ContentPreview({ fields, values, maxHeight, coverUrl, coverAlt }: {
  fields: AuthoringField[]
  values: Record<string, unknown>
  maxHeight: number
  coverUrl?: string
  coverAlt?: string
}) {
  const titleField = fields.find((f) => !f.rich && !f.long)
  return (
    <div style={{ maxHeight, overflowY: 'auto', padding: '10px 12px', background: '#fcfcfd', border: `1px solid ${BORDER}`, borderRadius: 8, fontSize: 14, lineHeight: 1.55, color: '#111827' }}>
      <style>{PREVIEW_CSS}</style>
      {coverUrl && (
        <img src={coverUrl} alt={coverAlt || ''} style={{ width: '100%', maxHeight: 220, objectFit: 'cover', borderRadius: 8, marginBottom: 8 }} />
      )}
      {titleField && str(values[titleField.key]) && (
        <div style={{ fontSize: 17, fontWeight: 700, marginBottom: 6 }}>{str(values[titleField.key])}</div>
      )}
      {fields.filter((f) => f !== titleField).map((f) => {
        const v = str(values[f.key])
        if (!v) return null
        if (f.rich) {
          return (
            <div key={f.key} className="twasta-composed-rich" style={{ margin: '6px 0' }}
              // Sanitised with the same allow-list the engine applies on write.
              dangerouslySetInnerHTML={{ __html: sanitizeRichHtml(v) }} />
          )
        }
        return (
          <div key={f.key} style={{ margin: '6px 0' }}>
            <div style={{ fontSize: 13, color: MUTED, textTransform: 'uppercase', letterSpacing: 0.3 }}>{f.label}</div>
            <div style={{ whiteSpace: 'pre-wrap' }}>{v}</div>
          </div>
        )
      })}
    </div>
  )
}

function FieldEditor({ field, value, disabled, onChange }: {
  field: AuthoringField | AuthoringColumn
  value: string
  disabled: boolean
  onChange: (v: string) => void
}) {
  const over = !!field.maxlen && value.length > field.maxlen
  const counter = field.maxlen ? (
    <span style={{ fontSize: 13, color: over ? '#dc2626' : MUTED, marginLeft: 6 }}>
      {value.length}/{field.maxlen}
    </span>
  ) : null
  const label = (
    <div style={{ fontSize: 13, color: MUTED, marginBottom: 3 }}>
      {field.label}{counter}
    </div>
  )
  const box: CSSProperties = {
    width: '100%', boxSizing: 'border-box', padding: '6px 8px', fontSize: 14,
    border: `1px solid ${over ? '#dc2626' : '#d1d5db'}`, borderRadius: 6, fontFamily: 'inherit',
  }
  if (field.rich) {
    return (
      <div>
        {label}
        <RichTextField value={value} onChange={onChange} disabled={disabled} minHeight={220} maxHeight={520} />
      </div>
    )
  }
  if (field.long) {
    return (
      <div>
        {label}
        <textarea value={value} disabled={disabled} rows={5} onChange={(e) => onChange(e.currentTarget.value)} style={{ ...box, resize: 'vertical' }} />
      </div>
    )
  }
  return (
    <div>
      {label}
      <input value={value} disabled={disabled} onChange={(e) => onChange(e.currentTarget.value)} style={box} />
    </div>
  )
}

export function ComposedContentCard({
  objLabel, action, summary, columns, values: initial, authoring,
  ran, ranPublished, runError, onRun, onRefine,
}: ComposedContentCardProps) {
  const [values, setValues] = useState<Record<string, unknown>>(() => ({ ...initial }))
  const [busy, setBusy] = useState<'' | 'draft' | 'publish' | 'refine'>('')
  const [outcome, setOutcome] = useState<{ ok: boolean; published: boolean; error?: string; pending?: string } | null>(
    ran ? { ok: ran === 'ok', published: !!ranPublished, error: runError } : null,
  )
  const review = authoring.publish_mode === 'review'
  const [editing, setEditing] = useState(false)
  const [expanded, setExpanded] = useState(review)
  const [feedback, setFeedback] = useState('')
  const [notes, setNotes] = useState<string[]>(() => authoring.notes || [])
  const [refineError, setRefineError] = useState('')

  const hasLifecycle = !!(authoring.status_column && authoring.draft_value && authoring.live_value)
  const isAdd = action.toLowerCase() === 'add'
  const contentKeys = useMemo(() => new Set(authoring.fields.map((f) => f.key)), [authoring.fields])
  // Fields the person may still need to fill: data-entry columns that are not
  // content and not the status (which the server decides).
  // A cover the Assistant generated is shown as a picture, not a URL field.
  const coverKey = authoring.image_field || ''
  const coverUrl = coverKey && authoring.composed.includes(coverKey) ? str(values[coverKey]) : ''
  const coverAlt = authoring.image_alt_field ? str(values[authoring.image_alt_field]) : ''
  const hidden = new Set(coverUrl ? [coverKey, authoring.image_alt_field || ''] : [])
  const otherColumns = columns.filter((c) => !c.auto && !contentKeys.has(c.key)
    && c.key !== authoring.status_column && !hidden.has(c.key))
  // Required content (a title) is checked too — empty rich text counts as empty.
  const missingRequired = [
    ...authoring.fields.filter((f) => f.required
      && !(f.rich ? richPlainText(values[f.key]) : str(values[f.key])).trim()),
    ...otherColumns.filter((c) => c.required && !str(values[c.key]).trim()),
  ]
  // Fields longer than their column allows — the save would be refused, so
  // the buttons wait; the footer says which and by how much (a disabled button
  // with no reason reads as "nothing happens").
  const overFields = [...authoring.fields, ...otherColumns].filter(
    (f) => !!f.maxlen && str(values[f.key]).length > (f.maxlen || 0))
  const overLimit = overFields.length > 0
  const overContent = overFields.some((f) => contentKeys.has(f.key))
  const done = !!outcome?.ok
  const locked = !!busy || done
  const blocked = missingRequired.length > 0 || overLimit

  const setField = (k: string, v: string) => setValues((vs) => ({ ...vs, [k]: v }))

  const run = async (publish: boolean) => {
    setBusy(publish ? 'publish' : 'draft')
    try {
      const r = await onRun({ ...values }, publish)
      setOutcome({ ok: r.ok, published: publish, error: r.error, pending: r.ok ? pendingApproval(r.result) : '' })
    } catch (e) {
      setOutcome({ ok: false, published: publish, error: describeRequestError(e) })
    } finally { setBusy('') }
  }

  const refine = async () => {
    if (!onRefine || !feedback.trim()) return
    setBusy('refine'); setRefineError('')
    try {
      const r = await onRefine({ ...values }, feedback.trim())
      if (r.ok && r.values) {
        setValues((vs) => ({ ...vs, ...r.values }))
        setNotes(r.notes || [])
        setFeedback('')
        setExpanded(true)
      } else {
        setRefineError(r.error || 'The draft could not be revised.')
      }
    } catch (e) {
      setRefineError(describeRequestError(e, 'The draft could not be revised.'))
    } finally { setBusy('') }
  }

  const words = authoring.fields.filter((f) => f.rich)
    .reduce((n, f) => n + richPlainText(values[f.key]).split(/\s+/).filter(Boolean).length, 0)
  const pictures = authoring.fields.filter((f) => f.rich)
    .reduce((n, f) => n + (str(values[f.key]).match(/<img\b/g) || []).length, 0) + (coverUrl ? 1 : 0)
  const titleField = authoring.fields.find((f) => !f.rich && !f.long)
  const heading = review ? `Review ${objLabel} before publishing`
    : isAdd ? `New ${objLabel}${hasLifecycle ? ' — draft' : ''}` : `Update ${objLabel}`

  return (
    <div style={{ border: `1px solid ${BORDER}`, borderRadius: 12, overflow: 'hidden', background: '#fff', minWidth: 300 }}>
      <div style={{ padding: '8px 12px', fontSize: 14, fontWeight: 600, background: '#f9fafb', borderBottom: `1px solid ${BORDER}` }}>
        {heading}
        {summary && <div style={{ fontWeight: 400, fontSize: 13, color: MUTED, marginTop: 2 }}>{summary}</div>}
      </div>

      <div style={{ padding: 10, display: 'grid', gap: 10 }}>
        {!review && !expanded && (
          <div style={{ fontSize: 14 }}>
            {titleField && <div style={{ fontWeight: 600 }}>{str(values[titleField.key]) || '(untitled)'}</div>}
            <div style={{ color: MUTED, fontSize: 13, marginTop: 2 }}>
              {words ? `About ${words} words written` : ''}
              {pictures ? `${words ? ', with' : 'With'} ${pictures} picture${pictures === 1 ? '' : 's'}. ` : words ? '. ' : ''}
              {hasLifecycle && isAdd ? `It will be saved as “${authoring.draft_value}” for you to review and publish later.` : ''}
            </div>
          </div>
        )}

        {expanded && !editing && <ContentPreview fields={authoring.fields} values={values} maxHeight={review ? 520 : 360} coverUrl={coverUrl} coverAlt={coverAlt} />}

        {editing && (
          <div style={{ display: 'grid', gap: 10 }}>
            {authoring.fields.map((f) => (
              <FieldEditor key={f.key} field={f} value={str(values[f.key])} disabled={locked} onChange={(v) => setField(f.key, v)} />
            ))}
          </div>
        )}

        {!done && (
          <div style={{ display: 'flex', gap: 14 }}>
            {!review && <button type="button" style={linkBtn} onClick={() => { setExpanded((x) => !x); setEditing(false) }}>{expanded ? 'Hide content' : 'Show content'}</button>}
            <button type="button" style={linkBtn} disabled={locked} onClick={() => { setEditing((x) => !x); setExpanded(true) }}>{editing ? 'Done editing' : 'Edit'}</button>
          </div>
        )}

        {otherColumns.length > 0 && !done && (
          <div style={{ display: 'grid', gap: 8 }}>
            {otherColumns.map((c) => {
              const v = str(values[c.key])
              const over = !!c.maxlen && v.length > c.maxlen
              return (
                <label key={c.key} style={{ display: 'grid', gridTemplateColumns: '120px 1fr', alignItems: 'center', gap: 8, fontSize: 14 }}>
                  <span style={{ color: MUTED, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {c.label}{c.required && <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>}
                  </span>
                  <span style={{ display: 'grid', gap: 2, minWidth: 0 }}>
                    <input value={v} disabled={locked} onChange={(e) => setField(c.key, e.currentTarget.value)}
                      style={{ padding: '5px 8px', border: `1px solid ${over ? '#dc2626' : '#d1d5db'}`, borderRadius: 6, fontSize: 14, minWidth: 0 }} />
                    {over && (
                      <span style={{ fontSize: 13, color: '#dc2626' }}>{v.length}/{c.maxlen} characters — too long</span>
                    )}
                  </span>
                </label>
              )
            })}
          </div>
        )}

        {review && onRefine && !done && (
          <div style={{ display: 'grid', gap: 6 }}>
            <div style={{ fontSize: 13, color: MUTED }}>Ask for changes</div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={feedback} disabled={locked} placeholder="e.g. make it shorter, add a section on pricing"
                onChange={(e) => setFeedback(e.currentTarget.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); void refine() } }}
                style={{ flex: 1, padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14 }} />
              <button type="button" onClick={() => void refine()} disabled={locked || !feedback.trim()}
                style={btn('#fff', '#111827', busy === 'refine', true)}>
                {busy === 'refine' ? 'Revising…' : 'Revise'}
              </button>
            </div>
            {refineError && <div style={{ fontSize: 13, color: '#b45309' }}>{refineError}</div>}
          </div>
        )}
        {notes.length > 0 && <div style={{ fontSize: 13, color: MUTED }}>{notes.join(' ')}</div>}
      </div>

      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: 10, borderTop: `1px solid ${BORDER}` }}>
        {!done && hasLifecycle && isAdd && review && (
          <button type="button" onClick={() => void run(true)} disabled={locked || blocked}
            style={btn(GREEN, '#fff', busy === 'publish', false, blocked)}>
            {busy === 'publish' ? 'Publishing…' : 'Publish'}
          </button>
        )}
        {!done && (
          <button type="button" onClick={() => void run(false)} disabled={locked || blocked}
            style={review && hasLifecycle && isAdd ? btn('#fff', '#111827', busy === 'draft', true, blocked) : btn(GREEN, '#fff', busy === 'draft', false, blocked)}>
            {busy === 'draft' ? 'Saving…'
              : hasLifecycle && isAdd ? 'Save as draft'
                : `${action.charAt(0).toUpperCase()}${action.slice(1)} ${objLabel}`}
          </button>
        )}
        {!done && missingRequired.length > 0 && (
          <div style={{ fontSize: 13, color: '#b45309' }}>Fill in {missingRequired.map((c) => c.label).join(', ')} first.</div>
        )}
        {!done && overLimit && (
          <div style={{ fontSize: 13, color: '#b45309' }}>
            Too long to save: {overFields.map((f) => `${f.label} (${str(values[f.key]).length}/${f.maxlen})`).join(', ')}.
            {' '}Shorten {overFields.length === 1 ? 'it' : 'them'} first.
            {overContent && !editing && (
              <> <button type="button" style={linkBtn} onClick={() => { setEditing(true); setExpanded(true) }}>Edit</button></>
            )}
          </div>
        )}
        {outcome && (
          <div style={{ fontSize: 14, color: outcome.ok ? GREEN : '#b45309' }}>
            {!outcome.ok ? `Couldn't save: ${outcome.error || 'the server refused it without giving a reason.'}`
              : outcome.pending ? outcome.pending
              : outcome.published ? `Published — the ${objLabel} is saved as “${authoring.live_value}”.`
                : hasLifecycle && isAdd ? `Saved as a draft. Review and publish it from the ${objLabel} screen.`
                  : 'Saved.'}
          </div>
        )}
      </div>
    </div>
  )
}

export default ComposedContentCard
